import { createElement, type ReactNode } from 'react'
import type { SegmentTone } from '@features/shared/components/SegmentedControl'
import {
  CalendarIcon,
  GripIcon,
  MonitorIcon,
  MoonIcon,
  SidebarCollapsedIcon,
  SidebarDynamicIcon,
  SidebarExpandedIcon,
  SortAlphabeticalIcon,
  SunIcon,
} from '@features/shared/icons/icons'

/** A sidebar list's sort: A to Z, by when last opened, or dragged into place. */
const SORT_METHOD_ICONS = {
  name: createElement(SortAlphabeticalIcon),
  recency: createElement(CalendarIcon),
  custom: createElement(GripIcon),
}

/**
 * The icon each option of a segmented preference shows, by preference key and
 * option value, on the Preferences pages and the sidebar's foot alike. The
 * segmented control sizes them. A preference whose options don't all have one
 * shows labels alone.
 */
export const PREFERENCE_SEGMENT_ICONS: Partial<Record<string, Partial<Record<string, ReactNode>>>> =
  {
    theme: {
      light: createElement(SunIcon),
      dark: createElement(MoonIcon),
      system: createElement(MonitorIcon),
    },
    sidebar_navigation_mode: {
      collapsed: createElement(SidebarCollapsedIcon),
      expanded: createElement(SidebarExpandedIcon),
      hover: createElement(SidebarDynamicIcon),
    },
    sidebar_table_sort: SORT_METHOD_ICONS,
    sidebar_favorites_sort: SORT_METHOD_ICONS,
  }

/** An option's colors: `tone` while selected, `hoverTone` while pointed at. */
export interface SegmentTones {
  tone?: SegmentTone
  hoverTone?: SegmentTone
}

const THEME_TONES: Record<'light' | 'dark', SegmentTones> = {
  light: { tone: 'yellow', hoverTone: 'yellow' },
  dark: { tone: 'info', hoverTone: 'info' },
}

/**
 * The colors some segmented preferences' options take, by option value (see
 * SegmentedControl). Theme's sun is yellow and its moon blue, and System takes
 * the colors of the theme it resolves to on this device (`systemTheme`, from
 * useSystemTheme), since that's the theme it shows. Options not listed, and
 * preferences without colors, take the defaults.
 */
export function preferenceSegmentTones(
  preferenceKey: string,
  systemTheme: 'light' | 'dark'
): Partial<Record<string, SegmentTones>> | undefined {
  if (preferenceKey === 'theme') return { ...THEME_TONES, system: THEME_TONES[systemTheme] }
  return undefined
}
